import {t} from '../../i18n/i18n.js';
import {FieldType, RenderMode, altKey, isFileFieldType} from '../../core/vocabulary.js';
import {toPreviewDocument} from './preview-document.js';

const VIEWPORT_WIDTHS = {
    wide: 1920,
    desktop: 1366,
    tablet: 768,
    mobile: 390
};
const VIEWPORT_HEIGHTS = {
    wide: 1080,
    desktop: 900,
    tablet: 1024,
    mobile: 844
};
const VIEWPORT_MODES = ['wide', 'desktop', 'tablet', 'mobile'];
const REFRESH_DELAY_MS = 450;

/**
 * Whether a template is a single image and nothing else, so its preview shows the chosen
 * image directly instead of asking the server to render it.
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @returns {boolean}
 */
function isImageOnlyTemplate(fields) {
    const textFields = fields.filter(f => !isFileFieldType(f.type));
    const imageFields = fields.filter(f => f.type === FieldType.IMAGE);
    return textFields.length === 0 && imageFields.length === 1;
}

/**
 * The live preview beside the page form: the sandboxed iframe, its viewport switcher and
 * scaling, the image-only preview, and the loading and outdated states. Requests a
 * server render through the `page-preview` event, which the page form answers with the
 * rendered HTML, and reports link clicks inside the preview as `preview-link-clicked`.
 */
export class PreviewPanel {
    #root;
    #emit;
    #getFields;
    #getMode;
    #getTemplateName;
    #renderId = 0;
    #rendered = false;
    #imageOnlyObjectUrl = null;
    #viewport = 'desktop';
    #scaleFrame = 0;
    #resizeObserver = null;
    #loadHandler = null;
    #refreshTimer = null;

    #onWindowResize = () => {
        this.#queueScaleUpdate();
    };

    #onPreviewMessage = (event) => {
        if (event?.data?.type !== 'wb-preview-link-clicked') {
            return;
        }
        const iframe = this.#$('#preview-frame');
        if (!iframe?.contentWindow || event.source !== iframe.contentWindow) {
            return;
        }
        this.#emit('preview-link-clicked', {
            href: event.data.href || ''
        });
    };

    /**
     * @param {{
     *   root: ShadowRoot,
     *   emit: (name: string, detail: Object) => void,
     *   getFields: () => import('../../shell/types/user.types.js').TemplateField[],
     *   getMode: () => RenderMode,
     *   getTemplateName: () => string,
     * }} options
     */
    constructor({root, emit, getFields, getMode, getTemplateName}) {
        this.#root = root;
        this.#emit = emit;
        this.#getFields = getFields;
        this.#getMode = getMode;
        this.#getTemplateName = getTemplateName;
    }

    #$(selector) {
        return this.#root.querySelector(selector);
    }

    /** Wires the preview controls once the page form's template is in the shadow root. */
    connect() {
        window.addEventListener('resize', this.#onWindowResize);
        window.addEventListener('message', this.#onPreviewMessage);
        this.#observeContainer();
        this.#$('#btn-preview')?.addEventListener('click', () => this.request());
        this.#$('#btn-show-preview')?.addEventListener('click', () => this.request());
        this.#$('#btn-hide-preview')?.addEventListener('click', () => this.#hide());
        for (const mode of VIEWPORT_MODES) {
            this.#$(`#btn-preview-${mode}`)?.addEventListener('click', () => this.#setViewport(mode));
        }
    }

    /** Applies the current viewport and schedules the first scale pass. */
    start() {
        this.#setViewport(this.#viewport);
        this.#queueScaleUpdate();
    }

    disconnect() {
        this.#cancelRefresh();
        window.removeEventListener('resize', this.#onWindowResize);
        window.removeEventListener('message', this.#onPreviewMessage);
        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
            this.#resizeObserver = null;
        }
        const iframe = this.#$('#preview-frame');
        if (iframe && this.#loadHandler) {
            iframe.removeEventListener('load', this.#loadHandler);
            this.#loadHandler = null;
        }
        if (this.#scaleFrame) {
            cancelAnimationFrame(this.#scaleFrame);
            this.#scaleFrame = 0;
        }
        this.#clearImageOnlyObjectUrl();
    }

    /** @param {string} html */
    render(html) {
        const iframe = this.#$('#preview-frame');
        const placeholder = this.#$('#preview-placeholder');
        if (!iframe) {
            return;
        }

        this.#setImageOnlyMode(false);
        this.#rendered = true;
        this.#setOutdated(false);
        const renderId = ++this.#renderId;
        const content = toPreviewDocument(html ?? '', {fragment: this.#getMode() === RenderMode.INDEX_FRAGMENT});
        if (placeholder) {
            placeholder.style.display = 'flex';
        }

        // Force a deterministic reload path; some browsers intermittently fail to repaint
        // when srcdoc is updated repeatedly with dynamic content.
        iframe.srcdoc = '';
        requestAnimationFrame(() => {
            if (renderId !== this.#renderId) {
                return;
            }
            if (this.#loadHandler) {
                iframe.removeEventListener('load', this.#loadHandler);
            }
            this.#loadHandler = () => {
                this.#finalizeRender(renderId, placeholder);
            };
            iframe.addEventListener('load', this.#loadHandler);
            iframe.srcdoc = content;
            setTimeout(() => this.#finalizeRender(renderId, placeholder), 0);
        });
    }

    /** @param {boolean} value */
    setLoading(value) {
        const previewLoading = this.#$('#preview-loading');
        if (previewLoading) {
            previewLoading.hidden = !value;
        }
        // The loading overlay is translucent; hide the empty-preview hint behind it.
        this.#$('#preview-placeholder')?.classList.toggle('is-loading', Boolean(value));
    }

    /**
     * Called when a preview request fails. Keeps the last successful preview on screen and
     * labels it as outdated; edits are untouched.
     * @returns {boolean} true when the last preview stays on screen, labelled as outdated
     */
    failed() {
        this.setLoading(false);
        if (this.#rendered) {
            this.#setOutdated(true);
        }
        return this.#rendered;
    }

    /** Refresh after typing settles, including the first change on a new page. */
    scheduleRefresh() {
        this.#cancelRefresh();
        if (this.#$('#preview-wrapper')?.hidden) return;
        this.#refreshTimer = setTimeout(() => {
            this.#refreshTimer = null;
            this.request();
        }, REFRESH_DELAY_MS);
    }

    #cancelRefresh() {
        if (this.#refreshTimer !== null) {
            clearTimeout(this.#refreshTimer);
            this.#refreshTimer = null;
        }
    }

    /** @param {boolean} outdated */
    #setOutdated(outdated) {
        const notice = this.#$('#preview-outdated');
        if (notice) notice.hidden = !outdated;
    }

    show() {
        const wrapper = this.#$('#preview-wrapper');
        const showBtn = this.#$('#btn-show-preview');
        if (wrapper) {
            wrapper.hidden = false;
        }
        if (showBtn) {
            showBtn.hidden = true;
        }
        this.#queueScaleUpdate();
    }

    #hide() {
        this.#cancelRefresh();
        const wrapper = this.#$('#preview-wrapper');
        const showBtn = this.#$('#btn-show-preview');
        if (wrapper) {
            wrapper.hidden = true;
        }
        if (showBtn) {
            showBtn.hidden = false;
        }
    }

    /** Drops the rendered preview and any pending render, back to the empty placeholder. */
    reset() {
        this.#cancelRefresh();
        this.#renderId++;
        this.#rendered = false;
        this.#setOutdated(false);
        const iframe = this.#$('#preview-frame');
        const placeholder = this.#$('#preview-placeholder');
        this.setLoading(false);
        this.#setImageOnlyMode(false);
        if (iframe) {
            iframe.srcdoc = '';
        }
        if (placeholder) placeholder.style.display = 'flex';
        this.#setPlaceholderText(t('pageForm.previewPlaceholder'));
    }

    /** Shows the preview and asks for a fresh render (or shows the image of an image-only template). */
    request() {
        this.#cancelRefresh();
        this.show();
        if (isImageOnlyTemplate(this.#getFields())) {
            this.#renderImageOnly();
            return;
        }
        this.setLoading(true);
        this.#emit('page-preview', {
            templateName: this.#getTemplateName(),
            formElement: this.#$('#page-form'),
            fields: this.#getFields(),
        });
    }

    #setViewport(mode) {
        this.#viewport = mode;
        for (const m of VIEWPORT_MODES) {
            const button = this.#$(`#btn-preview-${m}`);
            button?.classList.toggle('is-active', this.#viewport === m);
            button?.setAttribute('aria-pressed', String(this.#viewport === m));
        }
        const container = this.#$('.preview-container');
        if (container) {
            for (const m of VIEWPORT_MODES) {
                container.classList.toggle(`viewport-${m}`, this.#viewport === m);
            }
        }
        this.#queueScaleUpdate();
    }

    #observeContainer() {
        const container = this.#$('.preview-container');
        if (!container || typeof ResizeObserver === 'undefined') {
            return;
        }
        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
        }
        this.#resizeObserver = new ResizeObserver(() => {
            this.#queueScaleUpdate();
        });
        this.#resizeObserver.observe(container);
    }

    #queueScaleUpdate() {
        if (this.#scaleFrame) {
            cancelAnimationFrame(this.#scaleFrame);
        }
        this.#scaleFrame = requestAnimationFrame(() => {
            this.#scaleFrame = requestAnimationFrame(() => {
                this.#scaleFrame = 0;
                this.#updateScale();
            });
        });
    }

    #updateScale() {
        const container = this.#$('.preview-container');
        const stage = this.#$('#preview-stage');
        const iframe = this.#$('#preview-frame');
        if (!container || !stage) {
            return;
        }
        const viewportWidth = VIEWPORT_WIDTHS[this.#viewport];
        const viewportHeight = VIEWPORT_HEIGHTS[this.#viewport];
        const availableWidth = Math.max(container.clientWidth - 24, 0);
        const availableHeight = Math.max(container.clientHeight - 24, 0);
        if (availableWidth <= 0 || availableHeight <= 0) {
            return;
        }
        const widthScale = availableWidth / viewportWidth;
        const heightScale = availableHeight / viewportHeight;
        const scale = Math.min(1, widthScale, heightScale);
        const scaledWidth = Math.max(Math.round(viewportWidth * scale), 1);
        const scaledHeight = Math.max(Math.round(viewportHeight * scale), 1);

        stage.style.width = `${scaledWidth}px`;
        stage.style.minWidth = `${scaledWidth}px`;
        stage.style.height = `${scaledHeight}px`;

        if (iframe) {
            iframe.style.width = `${viewportWidth}px`;
            iframe.style.height = `${viewportHeight}px`;
            iframe.style.transform = `scale(${scale})`;
        }
    }

    #finalizeRender(renderId, placeholder) {
        if (renderId !== this.#renderId) {
            return;
        }
        if (placeholder) {
            placeholder.style.display = 'none';
        }
        this.setLoading(false);
        this.#queueScaleUpdate();
    }

    #setImageOnlyMode(enabled) {
        const iframe = this.#$('#preview-frame');
        const imageOnlyPreview = this.#$('#image-only-preview');
        if (iframe) iframe.hidden = enabled;
        if (imageOnlyPreview) imageOnlyPreview.hidden = !enabled;
        if (!enabled) this.#clearImageOnlyObjectUrl();
    }

    #clearImageOnlyObjectUrl() {
        if (this.#imageOnlyObjectUrl) {
            URL.revokeObjectURL(this.#imageOnlyObjectUrl);
            this.#imageOnlyObjectUrl = null;
        }
    }

    #renderImageOnly() {
        const placeholder = this.#$('#preview-placeholder');
        const field = this.#getFields().find(f => f.type === FieldType.IMAGE) ?? null;
        const imageInput = field ? this.#$(`[name="${field.key}"]`) : null;
        const altInput = field ? this.#$(`[name="${altKey(field.key)}"]`) : null;
        const imageOnly = this.#$('#image-only-preview');
        const imageEl = this.#$('#image-only-preview-img');

        if (!imageInput || !imageOnly || !imageEl) {
            return;
        }

        const caption = (altInput?.value || '').trim();
        imageEl.alt = caption || 'Template image preview';

        const currentFile = imageInput.files && imageInput.files.length > 0 ? imageInput.files[0] : null;
        const existingPath = imageInput.dataset.existingPath;

        this.#clearImageOnlyObjectUrl();

        if (currentFile) {
            this.#imageOnlyObjectUrl = URL.createObjectURL(currentFile);
            imageEl.src = this.#imageOnlyObjectUrl;
            if (placeholder) placeholder.style.display = 'none';
            this.setLoading(false);
            this.#setImageOnlyMode(true);
            return;
        }

        if (existingPath) {
            imageEl.src = '';
            this.setLoading(true);
            imageEl.onload = () => {
                this.setLoading(false);
            };
            imageEl.onerror = () => {
                this.setLoading(false);
            };
            this.#emit('asset-request', {path: existingPath, img: imageEl});
            if (placeholder) placeholder.style.display = 'none';
            this.#setImageOnlyMode(true);
            return;
        }

        if (placeholder) {
            placeholder.style.display = 'flex';
            this.#setPlaceholderText(t('pageForm.uploadImagePlaceholder'));
        }
        this.setLoading(false);
        this.#setImageOnlyMode(false);
    }

    #setPlaceholderText(text) {
        const placeholder = this.#$('#preview-placeholder');
        const placeholderText = placeholder?.querySelector('p');
        if (placeholderText) {
            placeholderText.textContent = text;
        }
    }
}
